/**
 * Report a session without the printed QR code — VH-138.
 *
 * The capture sheet at `/capture/session/:token` is public by design (VH-113):
 * the unguessable token IS the authorization, so it works in a gym with no
 * login. That design assumes the paper is in your pocket. This page is the
 * missing entry point for when it is not — a logged-in user picks this week's
 * session and lands on exactly the same sheet the QR opens.
 *
 * Deliberately NOT a second capture UI. Two places to enter sets would be two
 * places for the numbers to be wrong. Everything here stops at navigation.
 *
 * The asymmetry is intentional: this list is authenticated while the sheet it
 * links to is not. A token is a capability for one session; "show me my
 * sessions" is an account-level question, and a public version of it would
 * enumerate someone's training.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline'

import { weeklyProgramsAPI } from '../services/api'
import { currentIsoWeek, shiftIsoWeek } from '../utils/isoWeek'
import { sessionProgress } from '../utils/captureProgress'
import { fmtWeekday } from '../components/meal-prep/planFormat'
import { sessionDate } from '../utils/sessionDate'
import ReportedBadge from '../components/capture/ReportedBadge'
import UnattributedMachineReview from '../components/capture/UnattributedMachineReview'
import { useUnattributedExercises } from '../hooks/useUnattributedExercises'

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

export default function LogSession() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  // VH-154: the page was pinned to the current ISO week with no way to move.
  // Reto had weeks 33 and 34 open, closed 33, and could not reach 34 from his
  // phone — the sessions existed but the page could not ask for them. Reporting
  // routinely lags the training by a day or two, so "this week" is the right
  // default and the wrong constraint.
  const [viewed, setViewed] = useState(() => currentIsoWeek())
  const { year, week } = viewed
  const today = currentIsoWeek()
  const isCurrentWeek = today.year === year && today.week === week

  // VH-206: the review filter over this history — entries whose machine
  // nobody stated. The entry point exists only while there is a backlog (or
  // the filter is already on), so a user with no locations never sees it.
  const [machineFilter, setMachineFilter] = useState(false)
  const unattributed = useUnattributedExercises()
  const unattributedTotal = unattributed.data?.total ?? 0

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['weekly-program', 'week', year, week],
    queryFn: () => weeklyProgramsAPI.getProgramByWeek(year, week).then((r) => r.data),
    // A missing program is an answer, not a failure — the empty state below
    // explains it. Retrying a 404 four times only delays that message.
    retry: (count, err) => err?.response?.status !== 404 && count < 2,
  })

  const notFound = isError && error?.response?.status === 404
  const sessions = data?.sessions ?? []

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6" data-testid="log-session-loading">
        <div className="animate-pulse space-y-3">
          <div className="h-8 w-56 bg-gray-200 rounded" />
          <div className="h-24 bg-gray-200 rounded" />
          <div className="h-24 bg-gray-200 rounded" />
        </div>
      </div>
    )
  }

  if (isError && !notFound) {
    return (
      <div className="p-4 sm:p-6" data-testid="log-session-error">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('logSession.title')}</h1>
        <div className="card p-6 text-center">
          <ExclamationTriangleIcon className="h-10 w-10 mx-auto text-danger-600 mb-3" />
          <p className="text-gray-700 mb-4">{t('logSession.loadFailed')}</p>
          <button onClick={() => refetch()} className="btn-primary">
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto" data-testid="log-session">
      {/* Back to where the user came from. On a phone this page is opened from
          the nav or the quick-log sheet and, once inside, the only way out was
          the browser gesture — reported as "there is no back button". Falls back
          to Workouts when there is no history to pop (a fresh tab, or a link
          opened directly). */}
      <button
        type="button"
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/workouts'))}
        className="mb-3 inline-flex items-center text-sm text-gray-500 hover:text-gray-900"
        data-testid="log-session-back"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-1" />
        {t('common.back', 'Back')}
      </button>

      <h1 className="text-2xl font-bold text-gray-900">{t('logSession.title')}</h1>

      {(machineFilter || unattributedTotal > 0) && (
        <button
          type="button"
          aria-pressed={machineFilter}
          onClick={() => setMachineFilter((on) => !on)}
          data-testid="log-session-machine-filter"
          className={`mt-2 rounded-full border px-3 py-1 text-sm font-medium ${
            machineFilter
              ? 'border-primary-600 bg-primary-50 text-primary-700'
              : 'border-outline-variant text-gray-700 hover:bg-gray-100'
          }`}
        >
          {t('machineReview.filter', { count: unattributedTotal })}
        </button>
      )}

      {machineFilter ? (
        <div className="mt-4">
          <UnattributedMachineReview />
        </div>
      ) : (
      <>

      {/* VH-154: week navigation. Big tap targets — this is used one-handed in
          a gym, which is also why "Today" exists rather than expecting the user
          to count weeks back to the present. */}
      <div className="mt-1 mb-5 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setViewed(shiftIsoWeek(year, week, -1))}
          aria-label={t('logSession.previousWeek', { defaultValue: 'Previous week' })}
          data-testid="log-session-prev-week"
          className="p-2 rounded-md text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>

        <p className="text-gray-600 flex-1 text-center" data-testid="log-session-week">
          {t('logSession.subtitle', { week, year })}
        </p>

        <button
          type="button"
          onClick={() => setViewed(shiftIsoWeek(year, week, 1))}
          aria-label={t('logSession.nextWeek', { defaultValue: 'Next week' })}
          data-testid="log-session-next-week"
          className="p-2 rounded-md text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>

        {!isCurrentWeek && (
          <button
            type="button"
            onClick={() => setViewed(currentIsoWeek())}
            data-testid="log-session-today"
            className="text-sm font-medium text-primary-600 hover:text-primary-700 px-2"
          >
            {t('logSession.today', { defaultValue: 'Today' })}
          </button>
        )}
      </div>

      {notFound || sessions.length === 0 ? (
        <div className="card p-6 text-center" data-testid="log-session-empty">
          <CalendarDaysIcon className="h-10 w-10 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-700 mb-4">{t('logSession.noProgram')}</p>
          <button onClick={() => navigate('/weekly-programs')} className="btn-primary">
            {t('logSession.goToPrograms')}
          </button>
        </div>
      ) : (
        <ul className="space-y-3">
          {sessions.map((session) => {
            // No token means the sheet cannot be opened for this session.
            // Tokens are defaulted on creation, but older rows predate that,
            // and a dead link is worse than an honest "unavailable".
            const openable = Boolean(session.intake_token)
            const dayKey = session.planned_day ? DAY_KEYS[session.planned_day - 1] : null
            const date = sessionDate(session, data?.week_start_date)
            const progress = sessionProgress(session.exercises)

            return (
              <li key={session.id}>
                <button
                  type="button"
                  disabled={!openable}
                  data-testid={`log-session-${session.id}`}
                  onClick={() => navigate(`/capture/session/${session.intake_token}`)}
                  className={`card w-full text-left p-4 flex items-center justify-between gap-3 ${
                    openable ? 'hover:bg-surface-bright' : 'opacity-60 cursor-not-allowed'
                  }`}
                >
                  <div className="min-w-0">
                    {/* VH-182: wrap, never truncate. Session names are
                        distinguished at the END ("SET 2: Legs, Chest, Arms" vs
                        "SET 2: Lower Back, Chest, Legs"), so on a phone the
                        ellipsis made every row of a week read the same. Same
                        finding as VH-179, one component further out. */}
                    <div className="font-semibold text-gray-900 break-words">
                      {session.snapshot_module_name_en}
                    </div>
                    <div className="text-sm text-gray-600 mt-0.5" data-testid={`log-session-date-${session.id}`}>
                      {date
                        ? fmtWeekday(date, i18n?.language)
                        : dayKey ? t(`weekdays.${dayKey}`) : t('logSession.noDay')}
                      {session.snapshot_estimated_duration
                        ? ` · ${session.snapshot_estimated_duration} ${t('logSession.minutes')}`
                        : ''}
                    </div>
                    {!openable && (
                      <div className="text-sm text-warning-600 mt-1">
                        {t('logSession.unavailable')}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {/* VH-140: progress, not a yes/no tick. `is_completed` only
                        flips when the whole session is submitted, so a session
                        with three of five exercises in looked identical to an
                        untouched one — exactly the case Reto hits when he works
                        across two sessions. Falls back to `is_completed` when
                        the list payload carries no exercises. */}
                    {progress.total > 0 ? (
                      <span data-testid={`log-session-progress-${session.id}`}>
                        <ReportedBadge
                          state={progress.state}
                          reported={progress.reported}
                          total={progress.total}
                        />
                      </span>
                    ) : (
                      session.is_completed && (
                        <span
                          className="flex items-center text-sm text-success-600"
                          data-testid={`log-session-done-${session.id}`}
                        >
                          <CheckCircleIcon className="h-5 w-5 mr-1" />
                          {t('logSession.reported')}
                        </span>
                      )
                    )}
                    {openable && <ChevronRightIcon className="h-5 w-5 text-gray-400" />}
                  </div>
                </button>
              </li>
            )
          })}
        </ul>
      )}
      </>
      )}
    </div>
  )
}
